import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Inbox, Loader2, Mail, Phone, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("sml_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setEnquiries(data))
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("sml_token");
          navigate("/admin/login");
        } else {
          setError("Failed to load enquiries.");
          setEnquiries([]);
        }
      });
  }, [token, navigate]);

  const logout = () => {
    localStorage.removeItem("sml_token");
    navigate("/admin/login");
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-sml-mist">
      <header className="bg-sml-forest text-white">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-sm bg-white text-sml-forest font-display font-semibold">S</span>
            <span className="font-display font-semibold">SML Enquiries</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm border border-white/25 rounded-sm px-4 py-2 hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <Inbox className="h-6 w-6 text-sml-forest" />
          <h1 className="font-display text-2xl md:text-3xl text-sml-charcoal">Service Enquiries</h1>
          {enquiries && <span className="ml-2 font-mono-sml text-sm text-sml-green">{enquiries.length} total</span>}
        </div>

        {error && <p className="text-destructive" data-testid="admin-load-error">{error}</p>}

        {enquiries === null ? (
          <div className="flex items-center gap-2 text-sml-charcoal/60"><Loader2 className="h-5 w-5 animate-spin" /> Loading...</div>
        ) : enquiries.length === 0 ? (
          <div className="rounded-sm border border-sml-border bg-white p-12 text-center text-sml-charcoal/60" data-testid="admin-empty">
            No enquiries yet.
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((e) => (
              <article key={e.id} data-testid="enquiry-row" className="bg-white rounded-sm border border-sml-border p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-xl text-sml-charcoal">{e.full_name}</h3>
                    {e.company_name && <p className="text-sm text-sml-charcoal/60 flex items-center gap-1.5 mt-1"><Building2 className="h-3.5 w-3.5" /> {e.company_name}</p>}
                  </div>
                  <span className="font-mono-sml text-xs text-sml-charcoal/50">{fmt(e.created_at)}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="text-xs font-medium bg-sml-forest/5 text-sml-forest rounded-sm px-2.5 py-1">{e.service_required}</span>
                  <span className="text-xs font-medium bg-sml-bronze/15 text-sml-charcoal rounded-sm px-2.5 py-1">{e.property_type}</span>
                  <span className="text-xs font-medium border border-sml-border rounded-sm px-2.5 py-1">Prefers: {e.preferred_contact}</span>
                </div>
                <p className="mt-4 text-sm text-sml-charcoal/75 leading-relaxed">{e.message}</p>
                <div className="mt-4 flex flex-wrap gap-4 pt-4 border-t border-sml-border">
                  <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-sm text-sml-forest hover:underline"><Mail className="h-4 w-4" /> {e.email}</a>
                  <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 text-sm text-sml-forest hover:underline"><Phone className="h-4 w-4" /> {e.phone}</a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
